import React, { useState, useEffect, useRef } from 'react';
import { Toast } from 'primereact/toast';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Message } from 'primereact/message';
import { Menu } from 'primereact/menu';
import apiClient from '../../stateManagers/apiSetup/axiosInterceptor';
import { useAuth } from '../../stateManagers/services/AuthContext';
import CustomConfirmDialog from '../../components/CustomConfirmDialog';
import CourseChips from '../Subjects/parts/CourseChips';
import AddPracticalMaterialModal from './parts/AddPracticalMaterialModal';
import PracticalMaterialDetailsModal from './parts/PracticalMaterialDetailsModal';
import PracticalMaterialLibrarySidebar, { LibraryMaterial } from './parts/PracticalMaterialLibrarySidebar';

// ── Types ─────────────────────────────────────────────────────────────────────

interface PracticalFile {
  file_name: string;
  key: string;
  url: string;
  size: number;
  mime_type: string;
  uploaded_at: string;
}

interface HLSVideo {
  video_id: string;
  original_name: string;
  m3u8_url: string | null;
  size: number;
  status: string;
}

export interface PracticalMaterial {
  id: string;
  course_id: string;
  module_id: string | null;
  chapter_id: string | null;
  scope_type: 'chapter' | 'course';
  institute_id: string;
  branch_id: string | null;
  owner_level: 'institute' | 'branch';
  title: string;
  body: string;
  order: number;
  status: string;
  files: PracticalFile[];
  video_links: string[];
  videos: HLSVideo[];
  files_count: number;
  videos_count: number;
  created_by: string;
  created_by_role: 'super_admin' | 'admin' | 'operator';
  created_at: string;
}

interface Chapter {
  id: string;
  title: string;
  order: number;
  practical_materials: PracticalMaterial[];
  practical_materials_count: number;
}

interface Module {
  id: string;
  title: string;
  order: number;
  chapters: Chapter[];
  chapters_count: number;
}

interface Course {
  course_id: string;
  name: string;
  title: string;
  status: string;
}

// ── HCI brand tokens (from the logo) ──────────────────────────────────────────
const C = {
  navy: '#14267A', navyMid: '#2238A0', navyDeep: '#0B1650', gold: '#F5B326',
  page: '#F4F5FA', tint: '#E8EBF8', tintSoft: '#F4F5FA',
  line: '#E2E5F2', lineStrong: '#DADFF0',
  text: '#12183A', textSoft: '#5A6080',
};

// ── Permission helper ───────────────────────────────────────────────────────
// Super Admin panel: no restrictions — super_admin can manage everything.
export function canManagePracticalMaterial(
  _material: PracticalMaterial,
  _user: { id: string; role: string; institute_id?: string; branch_id?: string }
): boolean {
  return true;
}

// ── Component ──────────────────────────────────────────────────────────────────

const PracticalMaterials: React.FC = () => {
  const { user } = useAuth();
  const toast    = useRef<Toast>(null);
  const menuRefs = useRef<{ [key: string]: Menu | null }>({});

  const [modules,          setModules]          = useState<Module[]>([]);
  const [generalMaterials, setGeneralMaterials] = useState<PracticalMaterial[]>([]);
  const [loading,          setLoading]          = useState(false);
  const [error,            setError]            = useState<string | null>(null);
  const [selectedCourseId, setSelectedCourseId] = useState<string>('');
  const [selectedCourseName, setSelectedCourseName] = useState<string>('');
  const [selectedCourseTitle, setSelectedCourseTitle] = useState<string>('');

  const [showAddModal,     setShowAddModal]     = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);

  const [selectedChapterId, setSelectedChapterId] = useState<string | null>(null); // null = general
  const [selectedMaterial,  setSelectedMaterial]  = useState<PracticalMaterial | null>(null);

  const [showLibrarySidebar,   setShowLibrarySidebar]   = useState(false);
  const [reuseTargetChapterId, setReuseTargetChapterId] = useState<string | null>(null);
  const [reuseSource,          setReuseSource]          = useState<LibraryMaterial | null>(null);

  const [deleteDialog, setDeleteDialog] = useState<{ visible: boolean; id: string; title: string }>({
    visible: false, id: '', title: '',
  });

  // Inject PrimeReact menu style overrides once
  useEffect(() => {
    const style = document.createElement('style');
    style.innerHTML = `
      .p-menu { border-radius: 12px !important; border: 1px solid #DADFF0 !important; box-shadow: 0 8px 24px rgba(20,38,122,0.14) !important; padding: 6px !important; }
      .p-menu .p-menuitem-link { border-radius: 8px !important; padding: 9px 12px !important; margin: 2px 0 !important; transition: all 0.2s ease !important; }
      .p-menu .p-menuitem-link:hover { background: #EEF0F8 !important; }
      .p-menu .p-menuitem-icon { color: #14267A !important; font-size: 0.85rem !important; }
      .p-menu .p-menuitem-text { color: #12183A !important; font-weight: 600 !important; font-size: 0.8rem !important; }
    `;
    document.head.appendChild(style);
    return () => { document.head.removeChild(style); };
  }, []);

  useEffect(() => {
    if (selectedCourseId) {
      fetchCourseStructure();
    } else {
      setModules([]);
      setGeneralMaterials([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCourseId]);

  const fetchCourseStructure = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiClient.get(`/v1/practical-materials/course/${selectedCourseId}/structure`);
      if (response.data.success) {
        setModules(response.data.data.modules || []);
        setGeneralMaterials(response.data.data.general_materials || []);
      } else {
        setError('Failed to load practical materials');
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load practical materials');
    } finally {
      setLoading(false);
    }
  };

  const handleCourseSelect     = (course: Course) => {
    const deselect = course.course_id === selectedCourseId;
    setSelectedCourseId(deselect ? '' : course.course_id);
    setSelectedCourseName(deselect ? '' : course.name);
    setSelectedCourseTitle(deselect ? '' : course.title);
  };
  const handleAddGeneral       = () => { setSelectedChapterId(null); setSelectedMaterial(null); setShowAddModal(true); };
  const handleAddForChapter    = (chapterId: string) => { setSelectedChapterId(chapterId); setSelectedMaterial(null); setShowAddModal(true); };
  const handleView             = (material: PracticalMaterial) => { setSelectedMaterial(material); setShowDetailsModal(true); };
  const handleEdit             = (material: PracticalMaterial) => {
    setSelectedMaterial(material);
    setSelectedChapterId(material.chapter_id);
    setShowAddModal(true);
  };
  const handleDelete           = (id: string, title: string) => setDeleteDialog({ visible: true, id, title });

  const handleOpenLibrary = (chapterId: string | null) => {
    setReuseTargetChapterId(chapterId);
    setShowLibrarySidebar(true);
  };

  const handleReuseSelect = (material: LibraryMaterial) => {
    setReuseSource(material);
    setSelectedChapterId(reuseTargetChapterId);
    setSelectedMaterial(null);
    setShowLibrarySidebar(false);
    setShowAddModal(true);
  };

  const confirmDelete = async () => {
    try {
      await apiClient.delete(`/v1/practical-materials/${deleteDialog.id}`);
      toast.current?.show({ severity: 'success', summary: 'Deleted', detail: 'Practical material deleted successfully', life: 3000 });
      fetchCourseStructure();
      setDeleteDialog({ visible: false, id: '', title: '' });
    } catch (err: any) {
      toast.current?.show({ severity: 'error', summary: 'Error', detail: err.response?.data?.error || 'Failed to delete', life: 4000 });
    }
  };

  const getMaterialMenuItems = (material: PracticalMaterial) => [
    { label: 'View Details', icon: 'pi pi-eye',    command: () => handleView(material) },
    { label: 'Edit',         icon: 'pi pi-pencil', command: () => handleEdit(material) },
    { label: 'Delete',       icon: 'pi pi-trash',  command: () => handleDelete(material.id, material.title) },
  ];

  if (!user) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
        <ProgressSpinner style={{ width: '44px', height: '44px' }} strokeWidth="3" />
      </div>
    );
  }

  // ── Summary numbers ─────────────────────────────────────────────────────────
  const chapterMaterialsTotal = modules.reduce(
    (sum, m) => sum + m.chapters.reduce((s, c) => s + c.practical_materials_count, 0), 0
  );
  const totalMaterials = generalMaterials.length + chapterMaterialsTotal;
  const chaptersTotal  = modules.reduce((sum, m) => sum + m.chapters.length, 0);
  const allMaterials   = [
    ...generalMaterials,
    ...modules.flatMap((m) => m.chapters.flatMap((c) => c.practical_materials)),
  ];
  const filesTotal     = allMaterials.reduce((sum, m) => sum + (m.files_count || 0), 0);
  const videosTotal    = allMaterials.reduce((sum, m) => sum + (m.videos_count || 0), 0);

  const stats = [
    { icon: 'pi-folder-open', label: 'Materials', value: totalMaterials,  tag: 'Course', iconBg: C.tint,    iconFg: C.navy },
    { icon: 'pi-sitemap',     label: 'Chapters',  value: chaptersTotal,   tag: 'Course', iconBg: C.tint,    iconFg: C.navy },
    { icon: 'pi-paperclip',   label: 'Files',     value: filesTotal,      tag: 'Course', iconBg: '#E3F6C4', iconFg: '#2C5A12' },
    { icon: 'pi-play-circle', label: 'Videos',    value: videosTotal,     tag: 'Course', iconBg: '#FFE9BF', iconFg: '#8A4B00' },
  ];

  const primaryBtn = (disabled: boolean): React.CSSProperties => ({
    display: 'flex', alignItems: 'center', gap: '10px', height: '40px', padding: '0 20px',
    borderRadius: '12px', border: 'none', background: disabled ? '#B8BEDD' : C.navy, color: 'white',
    fontFamily: 'inherit', fontSize: '0.8rem', fontWeight: 700, cursor: disabled ? 'not-allowed' : 'pointer',
    boxShadow: disabled ? 'none' : '0 10px 22px rgba(20,38,122,0.28)', transition: 'background 0.2s ease',
  });
  const secondaryBtn = (disabled: boolean): React.CSSProperties => ({
    display: 'flex', alignItems: 'center', gap: '10px', height: '40px', padding: '0 18px',
    borderRadius: '12px', border: `1px solid ${C.lineStrong}`, background: 'white',
    color: disabled ? '#9AA0BE' : C.navy,
    fontFamily: 'inherit', fontSize: '0.8rem', fontWeight: 700, cursor: disabled ? 'not-allowed' : 'pointer',
    transition: 'background 0.2s ease',
  });

  return (
    <div style={{ padding: '0', margin: '0 auto', fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif", color: C.text }}>
      <Toast ref={toast} />

      <CustomConfirmDialog
        visible={deleteDialog.visible}
        onHide={() => setDeleteDialog({ visible: false, id: '', title: '' })}
        onConfirm={confirmDelete}
        title="Delete Practical Material"
        message={`Are you sure you want to delete "${deleteDialog.title}"? This action cannot be undone.`}
        confirmLabel="Delete"
        cancelLabel="Cancel"
        severity="danger"
        icon="pi pi-trash"
      />

      {/* ── Toolbar (page title + breadcrumb come from the app layout) ── */}
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: '12px', marginBottom: '10px' }}>
        <div style={{ fontSize: '0.78rem', color: C.textSoft, display: 'flex', alignItems: 'center', gap: '8px' }}>
          <i className="pi pi-file" style={{ color: C.navy, fontSize: '0.85rem' }}></i>
          {selectedCourseId
            ? <><strong style={{ color: C.text }}>{totalMaterials}</strong> practical material{totalMaterials !== 1 ? 's' : ''} in this course</>
            : 'Pick a course to manage its practical files'}
        </div>
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
          <button
            style={secondaryBtn(!selectedCourseId)}
            disabled={!selectedCourseId}
            onClick={() => handleOpenLibrary(null)}
            onMouseEnter={(e) => { if (selectedCourseId) e.currentTarget.style.background = C.tint; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'white'; }}
          >
            <i className="pi pi-copy" style={{ fontSize: '0.85rem' }}></i>
            Reuse Existing
          </button>
          <button
            style={primaryBtn(!selectedCourseId)}
            disabled={!selectedCourseId}
            onClick={handleAddGeneral}
            onMouseEnter={(e) => { if (selectedCourseId) e.currentTarget.style.background = C.navyDeep; }}
            onMouseLeave={(e) => { if (selectedCourseId) e.currentTarget.style.background = C.navy; }}
          >
            <i className="pi pi-plus" style={{ fontSize: '0.85rem' }}></i>
            Add General Material
          </button>
        </div>
      </div>

      {/* ── Course picker ── */}
      <div style={{ background: 'white', border: `1px solid ${C.line}`, borderRadius: '18px', padding: '14px 16px', marginBottom: '10px', boxShadow: '0 6px 18px rgba(20,38,122,0.05)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px' }}>
          <div style={{ width: '30px', height: '30px', borderRadius: '9px', background: C.tint, color: C.navy, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <i className="pi pi-filter" style={{ fontSize: '0.8rem' }}></i>
          </div>
          <h3 style={{ margin: 0, fontSize: '0.88rem', fontWeight: 700, letterSpacing: '-0.2px' }}>Select course</h3>
          {selectedCourseId && selectedCourseName && (
            <span style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '4px 12px', borderRadius: '999px', background: C.navy, color: 'white', fontSize: '0.72rem', fontWeight: 700, maxWidth: '420px', minWidth: 0 }}>
              <i className="pi pi-check-circle" style={{ fontSize: '0.75rem', color: C.gold, flexShrink: 0 }}></i>
              <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {selectedCourseName}
                {selectedCourseTitle && (
                  <span style={{ fontWeight: 500, color: 'rgba(255,255,255,0.75)' }}> · {selectedCourseTitle}</span>
                )}
              </span>
            </span>
          )}
          {selectedCourseId && (
            <button
              onClick={() => { setSelectedCourseId(''); setSelectedCourseName(''); setSelectedCourseTitle(''); }}
              style={{ marginLeft: 'auto', height: '30px', padding: '0 12px', background: 'white', border: `1px solid ${C.lineStrong}`, borderRadius: '10px', color: C.textSoft, fontFamily: 'inherit', fontSize: '0.72rem', fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}
            >
              <i className="pi pi-times" style={{ fontSize: '0.65rem' }} /> Clear
            </button>
          )}
        </div>
        <CourseChips selectedCourseId={selectedCourseId} onCourseSelect={handleCourseSelect} showSearch={false} />
      </div>

      {error && <Message severity="error" text={error} style={{ marginBottom: '16px', fontSize: '0.85rem', fontWeight: 500 }} />}

      {/* ── Content ── */}
      {!selectedCourseId ? (
        <EmptyState icon="pi pi-book" title="Select a course" subtitle="Choose a course above to view and manage its practical materials" />
      ) : loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '360px', background: C.page, borderRadius: '18px' }}>
          <div style={{ textAlign: 'center' }}>
            <ProgressSpinner style={{ width: '44px', height: '44px' }} strokeWidth="3" />
            <p style={{ marginTop: '16px', color: C.navy, fontSize: '0.85rem' }}>Loading practical materials...</p>
          </div>
        </div>
      ) : (
        <>
          {/* Summary */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px', marginBottom: '10px' }}>
            {stats.map((st) => (
              <div key={st.label} style={{ background: 'white', border: `1px solid ${C.line}`, borderRadius: '16px', padding: '12px 14px', display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div style={{ width: '34px', height: '34px', borderRadius: '10px', background: st.iconBg, color: st.iconFg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <i className={`pi ${st.icon}`} style={{ fontSize: '0.95rem' }}></i>
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: '1.15rem', fontWeight: 800, letterSpacing: '-0.4px', lineHeight: 1.1 }}>{st.value}</div>
                  <div style={{ fontSize: '0.7rem', color: C.textSoft, marginTop: '2px' }}>{st.label}</div>
                </div>
                <span style={{ padding: '2px 8px', borderRadius: '999px', background: '#EEF0F8', color: '#4A5070', fontSize: '0.62rem', fontWeight: 700 }}>{st.tag}</span>
              </div>
            ))}
          </div>

          {/* General (course-level) materials */}
          <Panel
            icon="pi-globe"
            title="General materials"
            count={generalMaterials.length}
            hint="Not tied to a specific chapter"
          >
            {generalMaterials.length === 0 ? (
              <p style={{ fontSize: '0.78rem', color: C.textSoft, margin: 0 }}>No general practical materials for this course yet.</p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {generalMaterials.map((material) => (
                  <MaterialRow
                    key={material.id}
                    material={material}
                    positionLabel={`G.${material.order}`}
                    menuRefs={menuRefs}
                    menuItems={getMaterialMenuItems(material)}
                    onView={() => handleView(material)}
                  />
                ))}
              </div>
            )}
          </Panel>

          {/* Modules > Chapters > chapter-scoped materials */}
          {modules.length === 0 ? (
            <EmptyState icon="pi pi-sitemap" title="No modules found" subtitle="Ask a super admin to create modules/chapters for this course first" />
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {modules.map((module) => (
                <ModuleBlock
                  key={module.id}
                  module={module}
                  menuRefs={menuRefs}
                  materialMenuItems={getMaterialMenuItems}
                  onAddForChapter={handleAddForChapter}
                  onReuseForChapter={(chapterId) => handleOpenLibrary(chapterId)}
                  onViewMaterial={handleView}
                />
              ))}
            </div>
          )}
        </>
      )}

      {selectedMaterial && (
        <PracticalMaterialDetailsModal
          visible={showDetailsModal}
          material={selectedMaterial}
          canManage={true}
          onHide={() => setShowDetailsModal(false)}
          onEdit={() => { setShowDetailsModal(false); setShowAddModal(true); }}
        />
      )}

      <AddPracticalMaterialModal
        visible={showAddModal}
        courseId={selectedCourseId}
        chapterId={selectedChapterId}
        editingMaterial={selectedMaterial}
        reuseSource={reuseSource}
        onHide={() => { setShowAddModal(false); setSelectedMaterial(null); setSelectedChapterId(null); setReuseSource(null); }}
        onSuccess={() => {
          toast.current?.show({ severity: 'success', summary: 'Success', detail: selectedMaterial ? 'Practical material updated!' : 'Practical material created!', life: 3000 });
          fetchCourseStructure();
          setShowAddModal(false);
          setSelectedMaterial(null);
          setSelectedChapterId(null);
          setReuseSource(null);
        }}
      />

      <PracticalMaterialLibrarySidebar
        visible={showLibrarySidebar}
        onHide={() => setShowLibrarySidebar(false)}
        onReuse={handleReuseSelect}
      />
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Sub-components
// ─────────────────────────────────────────────────────────────────────────────

const EmptyState: React.FC<{ icon: string; title: string; subtitle: string; children?: React.ReactNode }> = ({ icon, title, subtitle, children }) => (
  <div style={{ padding: '64px 20px', textAlign: 'center', background: 'white', borderRadius: '18px', border: `2px dashed ${C.lineStrong}` }}>
    <i className={icon} style={{ fontSize: '2.4rem', color: '#C6CDE8', marginBottom: '12px', display: 'block' }}></i>
    <h3 style={{ margin: '0 0 6px 0', color: C.textSoft, fontSize: '0.95rem', fontWeight: 700 }}>{title}</h3>
    <p style={{ margin: children ? '0 0 16px 0' : 0, color: '#9298B5', fontSize: '0.8rem' }}>{subtitle}</p>
    {children}
  </div>
);

const Panel: React.FC<{ icon: string; title: string; count: number; hint?: string; children: React.ReactNode }> = ({ icon, title, count, hint, children }) => (
  <div style={{ background: 'white', border: `1px solid ${C.line}`, borderRadius: '18px', padding: '16px', marginBottom: '10px', boxShadow: '0 6px 18px rgba(20,38,122,0.05)' }}>
    <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '10px', marginBottom: '12px' }}>
      <div style={{ width: '30px', height: '30px', borderRadius: '9px', background: C.tint, color: C.navy, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <i className={`pi ${icon}`} style={{ fontSize: '0.8rem' }}></i>
      </div>
      <h3 style={{ margin: 0, fontSize: '0.88rem', fontWeight: 700, letterSpacing: '-0.2px' }}>{title}</h3>
      <span style={{ padding: '2px 8px', borderRadius: '999px', background: '#EEF0F8', color: '#4A5070', fontSize: '0.62rem', fontWeight: 700 }}>{count}</span>
      {hint && <span style={{ fontSize: '0.7rem', color: C.textSoft }}>{hint}</span>}
    </div>
    {children}
  </div>
);

const OwnerBadge: React.FC<{ material: PracticalMaterial }> = ({ material }) => {
  const isInstitute = material.owner_level === 'institute';
  return (
    <span
      style={{
        fontSize: '0.62rem', fontWeight: 700, padding: '2px 8px', borderRadius: '999px',
        background: isInstitute ? '#FFE9BF' : '#E8EBF8',
        color: isInstitute ? '#8A4B00' : '#14267A',
      }}
    >
      {isInstitute ? 'Institute' : 'Branch'}
    </span>
  );
};

interface ModuleBlockProps {
  module: Module;
  menuRefs: React.MutableRefObject<{ [key: string]: Menu | null }>;
  materialMenuItems: (m: PracticalMaterial) => any[];
  onAddForChapter: (chapterId: string) => void;
  onReuseForChapter: (chapterId: string) => void;
  onViewMaterial: (m: PracticalMaterial) => void;
}

const ModuleBlock: React.FC<ModuleBlockProps> = ({ module, materialMenuItems, onAddForChapter, onReuseForChapter, onViewMaterial, menuRefs }) => {
  const [open, setOpen] = useState(true);
  const [hov, setHov]   = useState(false);
  const moduleTotal = module.chapters.reduce((s, c) => s + c.practical_materials_count, 0);

  return (
    <div
      style={{
        background: 'white', border: `1px solid ${hov ? '#C6D4FF' : C.line}`, borderRadius: '18px', overflow: 'hidden',
        boxShadow: hov ? '0 10px 24px rgba(20,38,122,0.10)' : '0 6px 18px rgba(20,38,122,0.05)',
        transition: 'border-color 0.25s ease, box-shadow 0.25s ease',
      }}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
    >
      {/* Module header */}
      <div
        role="button"
        tabIndex={0}
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setOpen((o) => !o); } }}
        style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '14px 16px', cursor: 'pointer', background: hov ? '#EEF3FF' : 'white', transition: 'background 0.25s ease' }}
      >
        <div style={{ width: '34px', height: '34px', borderRadius: '11px', background: C.navy, color: C.gold, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.85rem', fontWeight: 800, flexShrink: 0 }}>
          {module.order}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <h3 style={{ margin: 0, fontSize: '0.9rem', fontWeight: 700, letterSpacing: '-0.2px' }}>{module.title}</h3>
          <div style={{ fontSize: '0.7rem', color: C.textSoft, marginTop: '2px' }}>
            {module.chapters.length} chapter{module.chapters.length !== 1 ? 's' : ''} · {moduleTotal} material{moduleTotal !== 1 ? 's' : ''}
          </div>
        </div>
        <i className={`pi ${open ? 'pi-chevron-up' : 'pi-chevron-down'}`} style={{ color: C.navy, fontSize: '0.8rem' }}></i>
      </div>

      {/* Chapters */}
      {open && module.chapters.length > 0 && (
        <div style={{ padding: '4px 16px 16px', display: 'flex', flexDirection: 'column', gap: '10px', borderTop: `1px solid ${C.line}`, paddingTop: '14px' }}>
          {module.chapters.map((chapter) => (
            <div key={chapter.id} style={{ background: C.tintSoft, border: `1px solid ${C.line}`, borderRadius: '14px', padding: '12px' }}>
              <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '10px', marginBottom: '10px' }}>
                <div style={{ minWidth: '34px', height: '28px', padding: '0 8px', borderRadius: '9px', background: 'white', border: `1px solid ${C.lineStrong}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.7rem', fontWeight: 700, color: C.navy, fontFamily: "'JetBrains Mono', monospace" }}>
                  {module.order}.{chapter.order}
                </div>
                <h4 style={{ margin: 0, flex: 1, minWidth: 0, fontSize: '0.82rem', fontWeight: 700 }}>{chapter.title}</h4>
                <div style={{ display: 'flex', gap: '6px' }}>
                  <button
                    onClick={() => onReuseForChapter(chapter.id)}
                    style={{ height: '30px', padding: '0 12px', background: 'white', border: `1px solid ${C.lineStrong}`, borderRadius: '10px', color: C.navy, fontFamily: 'inherit', fontSize: '0.72rem', fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}
                  >
                    <i className="pi pi-copy" style={{ fontSize: '0.7rem' }} /> Reuse
                  </button>
                  <button
                    onClick={() => onAddForChapter(chapter.id)}
                    style={{ height: '30px', padding: '0 12px', background: C.navy, border: 'none', borderRadius: '10px', color: 'white', fontFamily: 'inherit', fontSize: '0.72rem', fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}
                  >
                    <i className="pi pi-plus" style={{ fontSize: '0.7rem' }} /> Add
                  </button>
                </div>
              </div>

              {chapter.practical_materials.length > 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {chapter.practical_materials.map((material) => (
                    <MaterialRow
                      key={material.id}
                      material={material}
                      positionLabel={`${module.order}.${chapter.order}.${material.order}`}
                      menuRefs={menuRefs}
                      menuItems={materialMenuItems(material)}
                      onView={() => onViewMaterial(material)}
                    />
                  ))}
                </div>
              ) : (
                <p style={{ fontSize: '0.75rem', color: C.textSoft, margin: 0 }}>No practical materials yet.</p>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

interface MaterialRowProps {
  material: PracticalMaterial;
  positionLabel: string;
  menuRefs: React.MutableRefObject<{ [key: string]: Menu | null }>;
  menuItems: any[];
  onView: () => void;
}

const MaterialRow: React.FC<MaterialRowProps> = ({ material, positionLabel, menuRefs, menuItems, onView }) => {
  const [hov, setHov] = useState(false);
  const readyVideos      = material.videos?.filter((v) => v.status === 'ready').length      ?? 0;
  const processingVideos = material.videos?.filter((v) => v.status === 'processing').length ?? 0;
  const menuKey = `practical-material-${material.id}`;

  const chip: React.CSSProperties = {
    display: 'flex', alignItems: 'center', gap: '5px', padding: '3px 9px', borderRadius: '9px',
    background: C.tintSoft, fontSize: '0.68rem', fontWeight: 600, color: C.text,
  };

  return (
    <div
      onClick={onView}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        padding: '10px 12px', background: hov ? '#EEF3FF' : 'white',
        border: `1px solid ${hov ? '#C6D4FF' : C.line}`, borderRadius: '13px', cursor: 'pointer',
        transition: 'background-color 0.25s ease, border-color 0.25s ease',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
        <div style={{ minWidth: '38px', height: '34px', padding: '0 6px', borderRadius: '10px', background: C.tint, color: C.navy, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontSize: '0.66rem', fontWeight: 700, fontFamily: "'JetBrains Mono', monospace" }}>
          {positionLabel}
        </div>

        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '8px', marginBottom: '5px' }}>
            <span style={{ fontSize: '0.82rem', fontWeight: 700, letterSpacing: '-0.1px' }}>{material.title}</span>
            <OwnerBadge material={material} />
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
            {material.files_count > 0 && (
              <span style={chip}>
                <i className="pi pi-paperclip" style={{ fontSize: '0.7rem', color: C.navy }}></i>
                {material.files_count} file{material.files_count !== 1 ? 's' : ''}
              </span>
            )}
            {readyVideos > 0 && (
              <span style={{ ...chip, background: '#E3F6C4', color: '#2C5A12', fontWeight: 700 }}>
                <i className="pi pi-play-circle" style={{ fontSize: '0.7rem' }}></i>
                {readyVideos} video{readyVideos !== 1 ? 's' : ''}
              </span>
            )}
            {processingVideos > 0 && (
              <span style={{ ...chip, background: '#FFE9BF', color: '#8A4B00', fontWeight: 700 }}>
                <i className="pi pi-spin pi-spinner" style={{ fontSize: '0.7rem' }}></i>
                {processingVideos} processing
              </span>
            )}
            {material.files_count === 0 && readyVideos === 0 && processingVideos === 0 && (
              <span style={{ fontSize: '0.68rem', color: C.textSoft }}>No attachments</span>
            )}
          </div>
        </div>

        <button
          aria-label="More actions"
          onClick={(e) => { e.stopPropagation(); menuRefs.current[menuKey]?.toggle(e); }}
          style={{ width: '32px', height: '32px', borderRadius: '10px', background: 'white', border: `1px solid ${C.lineStrong}`, color: C.navy, cursor: 'pointer', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        >
          <i className="pi pi-ellipsis-v" style={{ fontSize: '0.8rem' }}></i>
        </button>
        <Menu model={menuItems} popup ref={(el) => { menuRefs.current[menuKey] = el; }} />
      </div>
    </div>
  );
};

export default PracticalMaterials;